// FormSettingsSetup — the "Setup" tab of the Form settings drawer: form
// title/description, an intro-screen toggle, the owning-folder select, a
// form-icon picker, a submit-button-colour picker with a live preview, and the
// Approval-workflow section (workflow + version selects over
// GET /workflows/selection, with an info panel of the selected workflow's steps
// from GET /workflows/:id/details — the detail carries the *current* version's
// steps, the only source).
// Self-contained (its own local state, seeded from props); it remounts each
// time the drawer opens. Exposes an imperative save() to the drawer footer
// (PATCH /forms/update-form).

import { forwardRef, useEffect, useImperativeHandle, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Chip,
  Field,
  Icon,
  IconButton,
  Input,
  SelectDropdown,
  Switch,
  Textarea,
} from '@/components/common';
import { useUpdateFormMutation } from '@/api/endpoints/formsApi';
import { useGetFormFoldersQuery } from '@/api/endpoints/formFoldersApi';
import { useFormMediaMutation } from '@/api/endpoints/mediaApi';
import { uploadToPresignedUrl } from '@/utils/uploadFile';
import {
  useGetWorkflowDetailQuery,
  useGetWorkflowsSelectionQuery,
} from '@/api/endpoints/workflowsApi';
import type { FormSettingsTabHandle } from './FormSettingsDrawer';

interface FormSettingsSetupProps {
  /** The form being edited (PATCH target). */
  formId: string;
  /** Seed the fields from the form detail (the tab remounts each open). */
  initialTitle?: string;
  initialDescription?: string;
  initialIcon?: string;
  initialColor?: string;
  /** Seeded approval-workflow ids from the form detail (null → none). */
  initialWorkflow?: string | null;
  initialWorkflowVersion?: string | null;
  /** Seeded folder id from the form detail (null → no folder). */
  initialFolder?: string | null;
}

// The eight form-icon choices from the design.
const FORM_ICONS = [
  'calendar-check',
  'clipboard-check',
  'clipboard-list',
  'shield-check',
  'wrench',
  'truck',
  'box',
  'thermometer',
];

// Preset submit-button colours (a custom picker follows them).
const SUBMIT_COLORS = ['#2f9e6b', '#3b82f6', '#7c6cf0', '#e0a44a', '#d65563', '#1e2430'];

const formatFileSize = (bytes: number) =>
  bytes < 1024 * 1024
    ? `${Math.max(1, Math.round(bytes / 1024))} KB`
    : `${(bytes / (1024 * 1024)).toFixed(1)} MB`;

const FormSettingsSetup = forwardRef<FormSettingsTabHandle, FormSettingsSetupProps>(
  function FormSettingsSetup(
    {
      formId,
      initialTitle = '',
      initialDescription = '',
      initialIcon,
      initialColor,
      initialWorkflow,
      initialWorkflowVersion,
      initialFolder,
    },
    ref,
  ) {
    const { t } = useTranslation();
    const [title, setTitle] = useState(initialTitle);
    const [description, setDescription] = useState(initialDescription);
    // Not returned by the detail endpoint yet — always starts off.
    const [instructions, setInstructions] = useState(false);
    const [instructionsText, setInstructionsText] = useState('');
    // Reference file (one image or PDF), uploaded on save via media/form-media.
    const [refFile, setRefFile] = useState<File | null>(null);
    const fileInputRef = useRef<HTMLInputElement>(null);
    const [icon, setIcon] = useState(initialIcon || FORM_ICONS[0]);
    const [color, setColor] = useState(initialColor || SUBMIT_COLORS[0]);
    // Approval workflow (null = none) + its version (version id).
    const [workflowId, setWorkflowId] = useState<string | null>(initialWorkflow ?? null);
    const [versionId, setVersionId] = useState<string | null>(initialWorkflowVersion ?? null);
    // Owning folder — null = unassigned, which is also what the API wants.
    const [folder, setFolder] = useState<string | null>(initialFolder ?? null);
    const [submitted, setSubmitted] = useState(false);
    const [updateForm] = useUpdateFormMutation();
    const [formMedia] = useFormMediaMutation();

    // Preview URL for an image reference file; revoked when the file changes.
    const previewUrl = useMemo(
      () => (refFile && refFile.type !== 'application/pdf' ? URL.createObjectURL(refFile) : ''),
      [refFile],
    );
    useEffect(() => () => URL.revokeObjectURL(previewUrl), [previewUrl]);

    const acceptRefFile = (files: FileList | null) => {
      const f = files?.[0];
      if (f && (f.type.startsWith('image/') || f.type === 'application/pdf')) setRefFile(f);
    };

    // Folder choices — the tab only mounts with the drawer open, and this shares
    // the cache entry the Forms-page folders sidebar already populates.
    const { data: folders = [] } = useGetFormFoldersQuery();
    const folderOptions = useMemo(
      () => [
        { value: null, label: t('form_settings_folder_none'), icon: 'folder-open' },
        ...folders.map((f) => ({ value: f.id, label: f.title, icon: 'folder' })),
      ],
      [folders, t],
    );

    const { data: workflows = [] } = useGetWorkflowsSelectionQuery();
    const { data: workflowDetail } = useGetWorkflowDetailQuery(workflowId, { skip: !workflowId });
    const selectedWorkflow = workflows.find((w) => w.id === workflowId) ?? null;
    const selectedVersion = selectedWorkflow?.versions.find((v) => v.id === versionId) ?? null;

    const titleMissing = !title.trim();
    const versionMissing = Boolean(workflowId) && !versionId;

    // Changing the workflow resets the version — it's required, so the user
    // must pick one for the new workflow ('—' placeholder until then).
    const pickWorkflow = (id: string | null) => {
      setWorkflowId(id);
      setVersionId(null);
    };

    // Persist the Setup tab. Returns false on client validation failure so the
    // drawer keeps the tab open; throws on API error (drawer toasts it).
    useImperativeHandle(ref, () => ({
      save: async () => {
        setSubmitted(true);
        if (titleMissing || versionMissing) return false;

        // Instructions persist even with the toggle off, as long as a
        // description was written (isEnabled then records the off state).
        const keepInstructions = instructions || Boolean(instructionsText.trim());

        // Reference file: reserve a presigned URL, PUT the raw file, then send
        // the mediaId along. Any failure throws before the PATCH (drawer toasts).
        let media: string[] = [];
        if (keepInstructions && refFile) {
          const isPdf = refFile.type === 'application/pdf';
          const ext = refFile.name.split('.').pop() || (isPdf ? 'pdf' : 'png');
          const { uploadUrl, mediaId } = await formMedia({
            mediaType: isPdf ? 'file' : 'image',
            imageName: `${Date.now()}.${ext}`,
            formId,
          }).unwrap();
          await uploadToPresignedUrl(uploadUrl, refFile);
          media = [mediaId];
        }

        await updateForm({
          id: formId,
          body: {
            title: title.trim(),
            description,
            icon,
            submitButtonColor: color,
            // Always sent, like `folder` below — omitting the key would leave
            // the backend's existing value in place, so "No workflow" could
            // never actually detach one. Clearing the workflow clears its
            // version with it.
            workflow: workflowId,
            workflowVersion: workflowId ? versionId : null,
            folder,
            formInstructions: keepInstructions
              ? { isEnabled: instructions, description: instructionsText, media }
              : null,
          },
        }).unwrap();
        return true;
      },
    }));

    return (
      <div className='form-settings__setup'>
        <Field
          label={t('form_settings_form_title')}
          error={submitted && titleMissing ? t('forms_create_name_required') : undefined}
        >
          <Input
            value={title}
            error={submitted && titleMissing}
            onChange={(e) => setTitle(e.target.value)}
          />
        </Field>

        <div className='form-settings__field'>
          <div className='form-settings__label-row'>
            <span className='form-settings__label'>{t('form_settings_description')}</span>
            <span className='form-settings__optional'>{t('form_settings_optional')}</span>
          </div>
          <Textarea
            rows={4}
            value={description}
            placeholder={t('form_settings_description_ph')}
            onChange={(e) => setDescription(e.target.value)}
          />
        </div>

        {/* Approval workflow (optional) + required version once one is chosen. */}
        <div className='form-settings__field'>
          <div className='form-settings__label-row'>
            <span className='form-settings__label'>{t('form_settings_workflow')}</span>
            <span className='form-settings__optional'>{t('form_settings_optional')}</span>
          </div>
          <SelectDropdown
            iconStart='workflow'
            size='lg'
            aria-label={t('form_settings_workflow')}
            value={workflowId}
            options={[
              { value: null, label: t('form_settings_workflow_none') },
              ...workflows.map((w) => ({ value: w.id, label: w.title })),
            ]}
            onChange={(next) => pickWorkflow(next)}
          />
        </div>

        {workflowId && (
          <Field
            label={t('form_settings_workflow_version')}
            required
            htmlFor='form-workflow-version'
            error={
              submitted && versionMissing ? t('form_settings_workflow_version_required') : undefined
            }
          >
            <SelectDropdown
              id='form-workflow-version'
              iconStart='git-commit-horizontal'
              size='lg'
              placeholder='—'
              error={submitted && versionMissing}
              value={versionId}
              options={(selectedWorkflow?.versions ?? []).map((v) => ({
                value: v.id,
                label: `v${v.version}`,
              }))}
              onChange={(next) => setVersionId(next)}
            />
          </Field>
        )}

        {workflowId && workflowDetail && (
          <div className='form-settings__wf-panel'>
            <span className='form-settings__wf-panel-icon' aria-hidden='true'>
              <Icon name='info' size={17} />
            </span>
            <div className='form-settings__wf-panel-body'>
              <div className='form-settings__wf-head'>
                <span className='form-settings__wf-title'>
                  {workflowDetail.title}
                  {' · '}
                  {t('form_settings_workflow_steps', { count: workflowDetail.steps.length })}
                </span>
                {selectedVersion && <Chip variant='neutral'>{`v${selectedVersion.version}`}</Chip>}
              </div>
              {workflowDetail.steps.map((step, i) => (
                <div key={step.id || i} className='form-settings__wf-step'>
                  <span className='form-settings__wf-step-num'>{i + 1}</span>
                  <span className='form-settings__wf-step-title'>{step.title}</span>
                </div>
              ))}
              <p className='form-settings__wf-note'>{t('form_settings_workflow_note')}</p>
            </div>
          </div>
        )}

        {/* Intro-screen toggle card — expands with description + reference file. */}
        <div className={`form-settings__toggle-card${instructions ? ' is-on' : ''}`}>
          <div className='form-settings__toggle-row'>
            <span className='form-settings__toggle-icon' aria-hidden='true'>
              <Icon name='book-open' size={20} />
            </span>
            <span className='form-settings__toggle-text'>
              <span className='form-settings__toggle-title'>
                {t('form_settings_instructions_title')}
              </span>
              <span className='form-settings__toggle-desc'>
                {t('form_settings_instructions_desc')}
              </span>
            </span>
            <Switch
              checked={instructions}
              onChange={(e) => setInstructions(e.target.checked)}
              aria-label={t('form_settings_instructions_title')}
            />
          </div>

          {instructions && (
            <div className='form-settings__instructions-body'>
              <div className='form-settings__field'>
                <span className='form-settings__label'>{t('form_settings_description')}</span>
                <Textarea
                  rows={3}
                  value={instructionsText}
                  placeholder={t('form_settings_instructions_ph')}
                  onChange={(e) => setInstructionsText(e.target.value)}
                />
              </div>

              <div className='form-settings__field'>
                <div className='form-settings__label-row'>
                  <span className='form-settings__label'>{t('form_settings_reference_file')}</span>
                  <span className='form-settings__optional'>
                    {t('form_settings_reference_hint')}
                  </span>
                </div>
                {refFile ? (
                  <div className='form-settings__file-card'>
                    <div className='form-settings__file-head'>
                      <span className='form-settings__file-icon' aria-hidden='true'>
                        <Icon
                          name={refFile.type === 'application/pdf' ? 'file-text' : 'image'}
                          size={18}
                        />
                      </span>
                      <span className='form-settings__file-meta'>
                        <span className='form-settings__file-name'>{refFile.name}</span>
                        <span className='form-settings__file-sub'>
                          {refFile.type === 'application/pdf'
                            ? t('form_settings_reference_type_pdf')
                            : t('form_settings_reference_type_image')}
                          {' · '}
                          {formatFileSize(refFile.size)}
                        </span>
                      </span>
                      <IconButton
                        variant='ghost'
                        icon='refresh-cw'
                        aria-label={t('form_settings_reference_replace')}
                        onClick={() => fileInputRef.current?.click()}
                      />
                      <IconButton
                        variant='ghost'
                        icon='trash-2'
                        className='form-settings__file-remove'
                        aria-label={t('form_settings_reference_remove')}
                        onClick={() => setRefFile(null)}
                      />
                    </div>
                    {previewUrl && (
                      <div className='form-settings__file-preview'>
                        <img src={previewUrl} alt='' />
                      </div>
                    )}
                  </div>
                ) : (
                  <button
                    type='button'
                    className='form-settings__dropzone'
                    onClick={() => fileInputRef.current?.click()}
                  >
                    <span className='form-settings__dropzone-icon' aria-hidden='true'>
                      <Icon name='upload' size={20} />
                    </span>
                    <span className='form-settings__dropzone-title'>
                      {t('form_settings_reference_add')}
                    </span>
                    <span className='form-settings__dropzone-hint'>
                      {t('form_settings_reference_browse')}
                    </span>
                  </button>
                )}
                <input
                  ref={fileInputRef}
                  type='file'
                  accept='image/*,application/pdf'
                  className='form-settings__file-input'
                  onChange={(e) => {
                    acceptRefFile(e.target.files);
                    e.target.value = '';
                  }}
                />
              </div>
            </div>
          )}
        </div>

        {/* Owning folder — the Unassigned row's value IS null, saved as-is. */}
        <div className='form-settings__field'>
          <div className='form-settings__label-row'>
            <span className='form-settings__label'>{t('form_settings_folder')}</span>
            <span className='form-settings__optional'>{t('form_settings_folder_hint')}</span>
          </div>
          <SelectDropdown
            iconStart='folder'
            size='lg'
            portal
            flip
            aria-label={t('form_settings_folder')}
            value={folder}
            onChange={(next) => setFolder(next)}
            options={folderOptions}
          />
        </div>

        {/* Form icon picker. */}
        <div className='form-settings__field'>
          <span className='form-settings__label'>{t('form_settings_icon')}</span>
          <div className='form-settings__icons'>
            {FORM_ICONS.map((name) => (
              <button
                key={name}
                type='button'
                className={`form-settings__icon${icon === name ? ' is-selected' : ''}`}
                aria-label={name}
                aria-pressed={icon === name}
                onClick={() => setIcon(name)}
              >
                <Icon name={name} size={18} />
              </button>
            ))}
          </div>
        </div>

        {/* Submit-button colour picker + live preview. */}
        <div className='form-settings__field'>
          <span className='form-settings__label'>{t('form_settings_submit_color')}</span>
          <div className='form-settings__colors'>
            <div className='form-settings__swatches'>
              {SUBMIT_COLORS.map((c) => (
                <button
                  key={c}
                  type='button'
                  className={`form-settings__swatch${color === c ? ' is-selected' : ''}`}
                  style={{ background: c }}
                  aria-label={c}
                  aria-pressed={color === c}
                  onClick={() => setColor(c)}
                />
              ))}
              <label
                className={`form-settings__swatch form-settings__swatch--custom${
                  SUBMIT_COLORS.includes(color) ? '' : ' is-selected'
                }`}
                aria-label={t('form_settings_custom_color')}
              >
                <Icon name='plus' size={14} />
                <input
                  type='color'
                  className='form-settings__color-input'
                  value={color}
                  onChange={(e) => setColor(e.target.value)}
                />
              </label>
            </div>
            <button
              type='button'
              className='form-settings__submit-preview'
              style={{ background: color }}
              disabled
            >
              {t('form_settings_submit_preview')}
            </button>
          </div>
        </div>
      </div>
    );
  },
);

export default FormSettingsSetup;
